'use strict'

const GEOTIFF_CACHE_KEY_PREFIX_DEFAULT = 'portal-geotiff-cache-file-v1:'
const GEOTIFF_CACHE_INDEX_KEY_DEFAULT = 'portal-geotiff-cache-index-v1'

const logGeoTiffRestore = function (stage, details, level = 'info') {
  if (typeof console === 'undefined') return
  const normalizedLevel = String(level || 'info').toLowerCase()
  if (normalizedLevel === 'info') return
  const method = normalizedLevel === 'error'
    ? (typeof console.error === 'function' ? console.error : console.log)
    : (normalizedLevel === 'warn'
        ? (typeof console.warn === 'function' ? console.warn : console.log)
        : (typeof console.info === 'function' ? console.info : console.log))
  if (details === undefined) {
    method.call(console, '[GeoTIFF][Restore] ' + String(stage || ''))
    return
  }
  method.call(console, '[GeoTIFF][Restore] ' + String(stage || ''), details)
}

const resolveCacheKeyPrefix = function (rawValue) {
  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    const nestedValue = String(rawValue.GEOTIFF_CACHE_KEY_PREFIX || '').trim()
    return nestedValue || GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
  }
  const value = String(rawValue || '').trim()
  return value || GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
}

const buildGeoTiffSignature = function (file) {
  if (!file || typeof file !== 'object') return ''
  const name = String(file.name || '').trim()
  const size = Number(file.size || 0)
  const modified = Number(file.lastModified || 0)
  if (!name || !(size > 0)) return ''
  return name + '|' + size + '|' + modified
}

const normalizeGeoTiffDescriptor = function (rawDescriptor) {
  if (!rawDescriptor || typeof rawDescriptor !== 'object') return null
  if (rawDescriptor.unloaded === true) {
    return {
      unloaded: true
    }
  }
  const signature = String(rawDescriptor.signature || '').trim() || buildGeoTiffSignature(rawDescriptor)
  const name = String(rawDescriptor.name || '').trim()
  const sourcePath = String(rawDescriptor.sourcePath || rawDescriptor.path || '').trim()
  const size = Number(rawDescriptor.size || 0)
  const lastModified = Number(rawDescriptor.lastModified || 0)
  const type = String(rawDescriptor.type || 'image/tiff').trim() || 'image/tiff'
  if (!signature || !name || !(size > 0)) return null
  return {
    signature: signature,
    name: name,
    sourcePath: sourcePath,
    size: size,
    lastModified: Number.isFinite(lastModified) && lastModified > 0 ? lastModified : 0,
    type: type
  }
}

const buildGeoTiffDescriptor = function (file, signature) {
  return normalizeGeoTiffDescriptor({
    signature: String(signature || '').trim() || buildGeoTiffSignature(file),
    name: file && file.name,
    sourcePath: file && (file.sourcePath || file.path || file.webkitRelativePath || ''),
    size: file && file.size,
    lastModified: file && file.lastModified,
    type: file && file.type
  })
}

const buildGeoTiffFileFromDescriptor = function (descriptor) {
  const normalized = normalizeGeoTiffDescriptor(descriptor)
  if (!normalized) return null
  return {
    name: normalized.name,
    sourcePath: normalized.sourcePath,
    path: normalized.sourcePath,
    size: normalized.size,
    lastModified: normalized.lastModified,
    type: normalized.type
  }
}

const normalizeGeoTiffCacheEntry = function (signature, rawEntry, GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT) {
  if (!rawEntry || typeof rawEntry !== 'object') return null
  const descriptor = normalizeGeoTiffDescriptor({
    signature: signature,
    name: rawEntry.name,
    sourcePath: rawEntry.sourcePath || rawEntry.path || '',
    size: rawEntry.size,
    lastModified: rawEntry.lastModified,
    type: rawEntry.type
  })
  if (!descriptor) return null
  const normalizedEntry = {
    signature: descriptor.signature,
    key: String(rawEntry.key || cacheDataKey(descriptor.signature, GEOTIFF_CACHE_KEY_PREFIX)),
    name: descriptor.name,
    sourcePath: descriptor.sourcePath,
    size: descriptor.size,
    lastModified: descriptor.lastModified,
    lastUsedAt: Number(rawEntry.lastUsedAt || 0),
    type: descriptor.type
  }
  if (rawEntry.terrainMeta && typeof rawEntry.terrainMeta === 'object') {
    normalizedEntry.terrainMeta = Object.assign({}, rawEntry.terrainMeta)
  }
  return normalizedEntry
}

const resolveFileSignature = function (file, fnGeoTiffFileSignature = function () { return '' }) {
  const signature = String(fnGeoTiffFileSignature(file) || '').trim()
  return signature || buildGeoTiffSignature(file)
}

const cacheDataKey = function (signature, GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT) {
  return resolveCacheKeyPrefix(GEOTIFF_CACHE_KEY_PREFIX) + String(signature || '')
}

const refreshGeoTiffCacheMaxBytes = async function ({
  fnT = function (key) { return String(key || '') },
  fnGeoTiffLog = function () {},
  GEOTIFF_CACHE_MIN_BYTES = (128 * 1024 * 1024),
  GEOTIFF_CACHE_HARD_MAX_BYTES = (2 * 1024 * 1024 * 1024),
  GEOTIFF_CACHE_FALLBACK_MAX_BYTES = (512 * 1024 * 1024),
  navigatorRef = (window.navigator || null)
} = {}) {
  const minBytes = Number(GEOTIFF_CACHE_MIN_BYTES)
  const hardMaxBytes = Number(GEOTIFF_CACHE_HARD_MAX_BYTES)
  const fallbackBytes = Number(GEOTIFF_CACHE_FALLBACK_MAX_BYTES)
  if (!(navigatorRef && navigatorRef.storage && navigatorRef.storage.estimate)) {
    fnGeoTiffLog(fnT('log.geotiff_quota_unavailable_fallback'), { maxBytes: fallbackBytes })
    return fallbackBytes
  }
  try {
    const estimate = await navigatorRef.storage.estimate()
    const quota = Number(estimate && estimate.quota) || 0
    if (!(quota > 0)) {
      fnGeoTiffLog(fnT('log.geotiff_quota_missing_fallback'), { maxBytes: fallbackBytes })
      return fallbackBytes
    }
    const computed = Math.floor(quota * 0.25)
    const maxBytes = Math.max(minBytes, Math.min(hardMaxBytes, computed))
    fnGeoTiffLog(fnT('log.geotiff_quota_computed'), { quota: quota, maxBytes: maxBytes })
    return maxBytes
  } catch (_error) {
    fnGeoTiffLog(fnT('log.geotiff_quota_estimate_failed_fallback'), { maxBytes: fallbackBytes })
    return fallbackBytes
  }
}

const cacheReadIndex = async function ({
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  if (!idbKeyval) return { totalBytes: 0, entries: {}, activeDescriptor: null }
  const indexKey = String(GEOTIFF_CACHE_INDEX_KEY)
  const keyPrefix = resolveCacheKeyPrefix(GEOTIFF_CACHE_KEY_PREFIX)
  try {
    const raw = await idbKeyval.get(indexKey)
    const idx = (raw && typeof raw === 'object') ? raw : {}
    const entriesRaw = (idx.entries && typeof idx.entries === 'object') ? idx.entries : {}
    const entries = {}
    let totalBytes = 0
    Object.keys(entriesRaw).forEach((sig) => {
      const entry = normalizeGeoTiffCacheEntry(sig, entriesRaw[sig], keyPrefix)
      if (!entry) return
      entries[entry.signature] = entry
      totalBytes += entry.size
    })
    return {
      totalBytes: totalBytes,
      entries: entries,
      activeDescriptor: normalizeGeoTiffDescriptor(idx.activeDescriptor)
    }
  } catch (error) {
    console.error(fnT('log.geotiff_cache_index_read_error'), error)
    return { totalBytes: 0, entries: {}, activeDescriptor: null }
  }
}

const cacheWriteIndex = async function (index, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  if (!idbKeyval) return
  const indexKey = String(GEOTIFF_CACHE_INDEX_KEY)
  const keyPrefix = resolveCacheKeyPrefix(GEOTIFF_CACHE_KEY_PREFIX)
  const sourceEntries = (index && index.entries && typeof index.entries === 'object') ? index.entries : {}
  const entries = {}
  let totalBytes = 0
  Object.keys(sourceEntries).forEach((signature) => {
    const entry = normalizeGeoTiffCacheEntry(signature, sourceEntries[signature], keyPrefix)
    if (!entry) return
    entries[entry.signature] = entry
    totalBytes += entry.size
  })
  try {
    await idbKeyval.set(indexKey, {
      totalBytes: totalBytes,
      entries: entries,
      activeDescriptor: normalizeGeoTiffDescriptor(index && index.activeDescriptor)
    })
  } catch (error) {
    console.error(fnT('log.geotiff_cache_index_write_error'), error)
  }
}

const cacheTouch = async function (signature, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const normalizedSignature = String(signature || '').trim()
  if (!normalizedSignature) return
  const index = await cacheReadIndex({
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  const entry = index.entries[normalizedSignature]
  if (!entry) return
  entry.lastUsedAt = Date.now()
  await cacheWriteIndex(index, {
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
}

const cacheLoadBySignature = async function (signature, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  fnGeoTiffLog = function () {},
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT,
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT
} = {}) {
  const normalizedSignature = String(signature || '').trim()
  if (!normalizedSignature || !idbKeyval) {
    fnGeoTiffLog(fnT('log.geotiff_cache_load_skipped'), { hasSignature: !!normalizedSignature, hasIdb: !!idbKeyval })
    return null
  }
  const key = cacheDataKey(normalizedSignature, GEOTIFF_CACHE_KEY_PREFIX)
  try {
    fnGeoTiffLog(fnT('log.geotiff_cache_load_try'), { signature: normalizedSignature, key: key })
    const cached = await idbKeyval.get(key)
    if (!(cached instanceof ArrayBuffer)) {
      fnGeoTiffLog(fnT('log.geotiff_cache_miss'), {
        signature: normalizedSignature,
        reason: fnT('log.geotiff_cache_reason_not_arraybuffer'),
        cachedType: cached ? (cached.constructor && cached.constructor.name ? cached.constructor.name : typeof cached) : fnT('common.null')
      })
      return null
    }
    fnGeoTiffLog(fnT('log.geotiff_cache_hit'), { signature: normalizedSignature, bytes: cached.byteLength })
    void cacheTouch(normalizedSignature, {
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    return cached
  } catch (error) {
    console.error(fnT('log.geotiff_cache_load_error'), error)
    return null
  }
}

const cacheLoad = async function (file, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  fnGeoTiffLog = function () {},
  fnGeoTiffFileSignature = function () { return '' },
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT,
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT
} = {}) {
  const signature = resolveFileSignature(file, fnGeoTiffFileSignature)
  return cacheLoadBySignature(signature, {
    idbKeyval: idbKeyval,
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY
  })
}

const cacheStore = async function (file, arrayBuffer, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  fnGeoTiffLog = function () {},
  fnGeoTiffFileSignature = function () { return '' },
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT,
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_MIN_BYTES = (128 * 1024 * 1024),
  GEOTIFF_CACHE_HARD_MAX_BYTES = (2 * 1024 * 1024 * 1024),
  GEOTIFF_CACHE_FALLBACK_MAX_BYTES = (512 * 1024 * 1024),
  navigatorRef = (window.navigator || null)
} = {}) {
  if (!file || !(arrayBuffer instanceof ArrayBuffer) || !idbKeyval) {
    fnGeoTiffLog(fnT('log.geotiff_cache_store_skipped'), {
      hasFile: !!file,
      isArrayBuffer: (arrayBuffer instanceof ArrayBuffer),
      hasIdb: !!idbKeyval
    })
    return false
  }
  const fnCloneArrayBuffer = function (buffer) {
    if (!(buffer instanceof ArrayBuffer)) return null
    if (buffer.byteLength === 0) return null
    try {
      return buffer.slice(0)
    } catch (_error) {
      try {
        return Uint8Array.from(new Uint8Array(buffer)).buffer
      } catch (_error2) {
        return null
      }
    }
  }
  const storeBuffer = fnCloneArrayBuffer(arrayBuffer)
  if (!storeBuffer) {
    fnGeoTiffLog(fnT('log.geotiff_cache_store_clone_failed'))
    return false
  }
  const signature = resolveFileSignature(file, fnGeoTiffFileSignature)
  const key = cacheDataKey(signature, GEOTIFF_CACHE_KEY_PREFIX)
  const maxBytes = await refreshGeoTiffCacheMaxBytes({
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    GEOTIFF_CACHE_MIN_BYTES: GEOTIFF_CACHE_MIN_BYTES,
    GEOTIFF_CACHE_HARD_MAX_BYTES: GEOTIFF_CACHE_HARD_MAX_BYTES,
    GEOTIFF_CACHE_FALLBACK_MAX_BYTES: GEOTIFF_CACHE_FALLBACK_MAX_BYTES,
    navigatorRef: navigatorRef
  })
  if (!signature || !key) {
    fnGeoTiffLog(fnT('log.geotiff_cache_store_invalid_signature'))
    return false
  }
  const size = Number(file.size || storeBuffer.byteLength || 0)
  if (!(size > 0) || size > maxBytes) {
    fnGeoTiffLog(fnT('log.geotiff_cache_store_size_out_of_range'), { size: size, maxBytes: maxBytes })
    return false
  }
  fnGeoTiffLog(fnT('log.geotiff_cache_store_begin'), { signature: signature, size: size, maxBytes: maxBytes })
  const index = await cacheReadIndex({
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  const existing = index.entries[signature]
  if (existing && Number.isFinite(existing.size) && existing.size > 0) {
    index.totalBytes = Math.max(0, index.totalBytes - existing.size)
  }
  delete index.entries[signature]
  const candidates = Object.keys(index.entries).map((sig) => {
    const entry = index.entries[sig]
    return {
      signature: sig,
      size: Number(entry && entry.size) || 0,
      lastUsedAt: Number(entry && entry.lastUsedAt) || 0
    }
  }).sort((a, b) => a.lastUsedAt - b.lastUsedAt)
  for (let i = 0; i < candidates.length && (index.totalBytes + size > maxBytes); i++) {
    const victim = candidates[i]
    if (!(victim.size > 0)) continue
    try {
      fnGeoTiffLog(fnT('log.geotiff_cache_evict'), { signature: victim.signature, size: victim.size })
      await idbKeyval.del(cacheDataKey(victim.signature, GEOTIFF_CACHE_KEY_PREFIX))
    } catch (_error) {}
    delete index.entries[victim.signature]
    if (index.activeDescriptor && index.activeDescriptor.signature === victim.signature) {
      index.activeDescriptor = null
    }
    index.totalBytes = Math.max(0, index.totalBytes - victim.size)
  }
  if (index.totalBytes + size > maxBytes) {
    fnGeoTiffLog(fnT('log.geotiff_cache_store_no_space_after_evict'), {
      totalBytes: index.totalBytes,
      incomingSize: size,
      maxBytes: maxBytes
    })
    return false
  }
  try {
    await idbKeyval.set(key, storeBuffer)
    index.entries[signature] = {
      key: key,
      name: String(file.name || ''),
      sourcePath: String(file.sourcePath || file.path || file.webkitRelativePath || ''),
      size: size,
      lastModified: Number(file.lastModified || 0),
      lastUsedAt: Date.now(),
      type: String(file.type || 'image/tiff').trim() || 'image/tiff',
      terrainMeta: (existing && existing.terrainMeta && typeof existing.terrainMeta === 'object')
        ? Object.assign({}, existing.terrainMeta)
        : undefined
    }
    index.totalBytes += size
    await cacheWriteIndex(index, {
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    fnGeoTiffLog(fnT('log.geotiff_cache_store_success'), {
      signature: signature,
      storedBytes: size,
      totalBytes: index.totalBytes,
      entries: Object.keys(index.entries).length
    })
    return true
  } catch (error) {
    console.error(fnT('log.geotiff_cache_store_error'), error)
    return false
  }
}

const readGeoTiffCacheEntry = async function (signature, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const normalizedSignature = String(signature || '').trim()
  if (!normalizedSignature) return null
  const index = await cacheReadIndex({
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  return index && index.entries && index.entries[normalizedSignature]
    ? Object.assign({}, index.entries[normalizedSignature])
    : null
}

const readGeoTiffTerrainMeta = async function (signature, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const entry = await readGeoTiffCacheEntry(signature, {
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  return (entry && entry.terrainMeta && typeof entry.terrainMeta === 'object')
    ? Object.assign({}, entry.terrainMeta)
    : null
}

const writeGeoTiffTerrainMeta = async function (signature, terrainMeta, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const normalizedSignature = String(signature || '').trim()
  if (!normalizedSignature || !idbKeyval) return false
  try {
    const index = await cacheReadIndex({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    if (!index || !index.entries || typeof index.entries !== 'object') return false
    const entry = index.entries[normalizedSignature]
    if (!entry || typeof entry !== 'object') return false
    if (terrainMeta && typeof terrainMeta === 'object') {
      entry.terrainMeta = Object.assign({}, terrainMeta)
    } else {
      delete entry.terrainMeta
    }
    await cacheWriteIndex(index, {
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    return true
  } catch (_error) {
    return false
  }
}

const readGeoTiffActiveDescriptor = async function ({
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const index = await cacheReadIndex({
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  const descriptor = normalizeGeoTiffDescriptor(index && index.activeDescriptor)
  logGeoTiffRestore('read active descriptor', {
    hasIdb: !!idbKeyval,
    descriptor: descriptor,
    entryCount: index && index.entries ? Object.keys(index.entries).length : 0,
    totalBytes: Number(index && index.totalBytes) || 0
  }, descriptor ? 'info' : 'warn')
  return descriptor
}

const writeGeoTiffActiveDescriptor = async function (rawDescriptor, {
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  if (!idbKeyval) return false
  const descriptor = normalizeGeoTiffDescriptor(rawDescriptor) || buildGeoTiffDescriptor(rawDescriptor)
  if (!descriptor) return false
  try {
    const index = await cacheReadIndex({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    index.activeDescriptor = descriptor
    await cacheWriteIndex(index, {
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    logGeoTiffRestore('write active descriptor', descriptor)
    return true
  } catch (error) {
    logGeoTiffRestore('write active descriptor failed', {
      descriptor: descriptor,
      error: String((error && error.message) || error || '')
    }, 'error')
    return false
  }
}

const clearGeoTiffActiveDescriptor = async function ({
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  if (!idbKeyval) return false
  try {
    const index = await cacheReadIndex({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    index.activeDescriptor = null
    await cacheWriteIndex(index, {
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    logGeoTiffRestore('clear active descriptor')
    return true
  } catch (error) {
    logGeoTiffRestore('clear active descriptor failed', {
      error: String((error && error.message) || error || '')
    }, 'error')
    return false
  }
}

const resolveGeoTiffActiveRestoreCandidate = async function ({
  idbKeyval,
  fnT = function (key) { return String(key || '') },
  fnGeoTiffLog = function () {},
  allowDescriptorOnly = false,
  GEOTIFF_CACHE_INDEX_KEY = GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  GEOTIFF_CACHE_KEY_PREFIX = GEOTIFF_CACHE_KEY_PREFIX_DEFAULT
} = {}) {
  const index = await cacheReadIndex({
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  let descriptor = normalizeGeoTiffDescriptor(index && index.activeDescriptor)
  if (descriptor && descriptor.unloaded === true) {
    logGeoTiffRestore('resolve candidate aborted', {
      reason: 'explicitly_unloaded'
    }, 'warn')
    return null
  }
  if (!descriptor) {
    const entries = (index && index.entries && typeof index.entries === 'object') ? index.entries : {}
    const fallbackSignature = Object.keys(entries).sort(function (a, b) {
      return Number((entries[b] && entries[b].lastUsedAt) || 0) - Number((entries[a] && entries[a].lastUsedAt) || 0)
    })[0] || ''
    if (fallbackSignature) {
      const fallbackEntry = entries[fallbackSignature] || null
      descriptor = normalizeGeoTiffDescriptor(Object.assign({}, fallbackEntry || {}, { signature: fallbackSignature }))
    }
  }
  if (!descriptor) {
    logGeoTiffRestore('resolve candidate aborted', {
      reason: 'no_active_descriptor',
      entryCount: index && index.entries ? Object.keys(index.entries).length : 0
    }, 'warn')
    return null
  }
  const fnBuildDescriptorOnlyCandidate = function (entryOverride) {
    if (allowDescriptorOnly !== true) return null
    const entry = (entryOverride && typeof entryOverride === 'object') ? entryOverride : null
    const file = buildGeoTiffFileFromDescriptor(entry) || buildGeoTiffFileFromDescriptor(descriptor)
    if (!file || !String(file.sourcePath || file.path || '').trim()) return null
    return {
      signature: descriptor.signature,
      descriptor: descriptor,
      entry: entry,
      file: file,
      arrayBuffer: null,
      descriptorOnly: true
    }
  }
  const entry = await readGeoTiffCacheEntry(descriptor.signature, {
    idbKeyval: idbKeyval,
    fnT: fnT,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  if (!entry) {
    const descriptorOnlyCandidate = fnBuildDescriptorOnlyCandidate(null)
    if (descriptorOnlyCandidate) {
      logGeoTiffRestore('resolve candidate descriptor-only', {
        reason: 'missing_cache_entry',
        signature: descriptor.signature,
        descriptor: descriptor
      }, 'warn')
      return descriptorOnlyCandidate
    }
    logGeoTiffRestore('resolve candidate aborted', {
      reason: 'missing_cache_entry',
      descriptorOnlyBlockedOnWeb: allowDescriptorOnly !== true,
      signature: descriptor.signature,
      descriptor: descriptor
    }, 'warn')
    fnGeoTiffLog('GeoTIFF active cache descriptor missing entry', { signature: descriptor.signature })
    await clearGeoTiffActiveDescriptor({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    return null
  }
  const arrayBuffer = await cacheLoadBySignature(descriptor.signature, {
    idbKeyval: idbKeyval,
    fnT: fnT,
    fnGeoTiffLog: fnGeoTiffLog,
    GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
    GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
  })
  if (!(arrayBuffer instanceof ArrayBuffer)) {
    const descriptorOnlyCandidate = fnBuildDescriptorOnlyCandidate(entry)
    if (descriptorOnlyCandidate) {
      logGeoTiffRestore('resolve candidate descriptor-only', {
        reason: 'missing_arraybuffer',
        signature: descriptor.signature,
        entry: {
          key: entry.key,
          size: entry.size
        }
      }, 'warn')
      return descriptorOnlyCandidate
    }
    logGeoTiffRestore('resolve candidate aborted', {
      reason: 'missing_arraybuffer',
      descriptorOnlyBlockedOnWeb: allowDescriptorOnly !== true,
      signature: descriptor.signature,
      entry: {
        key: entry.key,
        size: entry.size
      }
    }, 'warn')
    fnGeoTiffLog('GeoTIFF active cache descriptor missing arrayBuffer', { signature: descriptor.signature })
    await clearGeoTiffActiveDescriptor({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    return null
  }
  if (Number(entry.size || 0) > 0 && Number(arrayBuffer.byteLength || 0) > 0 && Number(entry.size || 0) !== Number(arrayBuffer.byteLength || 0)) {
    const descriptorOnlyCandidate = fnBuildDescriptorOnlyCandidate(entry)
    if (descriptorOnlyCandidate) {
      logGeoTiffRestore('resolve candidate descriptor-only', {
        reason: 'byte_length_mismatch',
        signature: descriptor.signature,
        expectedBytes: Number(entry.size || 0),
        actualBytes: Number(arrayBuffer.byteLength || 0)
      }, 'warn')
      return descriptorOnlyCandidate
    }
    logGeoTiffRestore('resolve candidate aborted', {
      reason: 'byte_length_mismatch',
      descriptorOnlyBlockedOnWeb: allowDescriptorOnly !== true,
      signature: descriptor.signature,
      expectedBytes: Number(entry.size || 0),
      actualBytes: Number(arrayBuffer.byteLength || 0)
    }, 'warn')
    fnGeoTiffLog('GeoTIFF active cache descriptor byteLength mismatch', {
      signature: descriptor.signature,
      expectedBytes: Number(entry.size || 0),
      actualBytes: Number(arrayBuffer.byteLength || 0)
    })
    await clearGeoTiffActiveDescriptor({
      idbKeyval: idbKeyval,
      fnT: fnT,
      GEOTIFF_CACHE_INDEX_KEY: GEOTIFF_CACHE_INDEX_KEY,
      GEOTIFF_CACHE_KEY_PREFIX: GEOTIFF_CACHE_KEY_PREFIX
    })
    return null
  }
  const candidate = {
    signature: descriptor.signature,
    descriptor: descriptor,
    entry: entry,
    file: buildGeoTiffFileFromDescriptor(entry) || buildGeoTiffFileFromDescriptor(descriptor),
    arrayBuffer: arrayBuffer
  }
  return candidate
}

export {
  GEOTIFF_CACHE_KEY_PREFIX_DEFAULT,
  GEOTIFF_CACHE_INDEX_KEY_DEFAULT,
  buildGeoTiffSignature,
  buildGeoTiffDescriptor,
  buildGeoTiffFileFromDescriptor,
  cacheDataKey,
  refreshGeoTiffCacheMaxBytes,
  cacheReadIndex,
  cacheWriteIndex,
  cacheTouch,
  cacheLoadBySignature,
  cacheLoad,
  cacheStore,
  readGeoTiffCacheEntry,
  readGeoTiffTerrainMeta,
  writeGeoTiffTerrainMeta,
  readGeoTiffActiveDescriptor,
  writeGeoTiffActiveDescriptor,
  clearGeoTiffActiveDescriptor,
  resolveGeoTiffActiveRestoreCandidate
}
